import { useCallback, useEffect, useRef, useState } from 'react';
import './RepoConsole.css';
import useStickToBottom from './useStickToBottom';
import { resolveAgentBackend } from '../lib/agentBackend';

// Voice dashboard that drives the local agent backend (agent-server/).
// Speak -> Web Speech STT -> WS to the backend -> Claude Agent SDK edits a
// local repo -> reply text -> ElevenLabs TTS -> spoken back.
//
// Security: the backend runs on localhost and is gated by AGENT_TOKEN. This
// page just needs the token + backend URL (stored in localStorage).

// Backend URL is auto-derived from where the app is loaded (local loopback vs
// the tailnet :8443 proxy) — see ../lib/agentBackend. Token can be baked into the
// repo's gitignored .env.local so you never re-enter it. localStorage (what you
// last typed) wins; the env var is the fallback seed.
const ENV_TOKEN = process.env.REACT_APP_AGENT_TOKEN || '';

// Connection resilience tuning (mirrors GlobalAIAgent.js, with jitter added).
const MAX_RECONNECT_ATTEMPTS = 8;
const MAX_RECONNECT_DELAY_MS = 15000;
const CONNECT_TIMEOUT_MS = 10000;
const CLIENT_PING_INTERVAL_MS = 20000;
const STALE_AFTER_MS = 45000;
const STALE_CHECK_INTERVAL_MS = 15000;
const SS_KEYS = { id: 'agentSessionId', seq: 'agentLastSeq', repo: 'agentSessionRepo' };

function wsUrlFrom(httpUrl, token) {
  const u = new URL(httpUrl);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = '/agent';
  u.search = `?token=${encodeURIComponent(token)}`;
  return u.toString();
}

export default function RepoConsole() {
  const [backendUrl, setBackendUrl] = useState(
    () => resolveAgentBackend(localStorage.getItem('agentBackendUrl'))
  );
  const [token, setToken] = useState(() => localStorage.getItem('agentToken') || ENV_TOKEN);
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [connStatus, setConnStatus] = useState('idle'); // 'idle'|'connecting'|'connected'|'reconnecting'|'failed'
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [sessionReady, setSessionReady] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [transcript, setTranscript] = useState([]); // {who, text}
  const [activity, setActivity] = useState({ items: [], total: 0 }); // items:{tool,summary}[], total monotonic
  const [perms, setPerms] = useState([]); // {id, tool, command, title, reason}
  const [status, setStatus] = useState('Idle');
  const [muted, setMuted] = useState(false);

  // Independent stick-to-bottom for each pane. extraDep=interim re-sticks the
  // conversation while the live caption grows without inflating newCount.
  const convScroll = useStickToBottom(transcript.length, interim);
  const actScroll = useStickToBottom(activity.total);

  const wsRef = useRef(null);
  const recognitionRef = useRef(null);
  const ttsQueueRef = useRef([]);
  const playingRef = useRef(false);
  const audioRef = useRef(null);
  const liveBufRef = useRef(''); // accumulates text-delta into a live caption

  // Connection resilience (reconnect / keepalive / connect timeout / resume)
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const connectTimeoutRef = useRef(null);
  const keepaliveTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const lastActivityRef = useRef(0);
  const connectSocketRef = useRef(null);
  const handleMessageRef = useRef(null);
  const selectedRepoRef = useRef('');
  const sessionIdRef = useRef(sessionStorage.getItem(SS_KEYS.id) || null);
  const lastSeqRef = useRef(Number(sessionStorage.getItem(SS_KEYS.seq)) || 0);

  // Keep the repo ref in sync so onopen / resume read the latest selection.
  useEffect(() => {
    selectedRepoRef.current = selectedRepo;
  }, [selectedRepo]);

  const persist = (k, v) => localStorage.setItem(k, v);

  // ---- session persistence (survives page reloads while the grace window holds) ----
  const persistSession = (id, seq, repoPath) => {
    sessionStorage.setItem(SS_KEYS.id, id);
    sessionStorage.setItem(SS_KEYS.seq, String(seq));
    sessionStorage.setItem(SS_KEYS.repo, repoPath);
  };

  const clearSession = () => {
    sessionStorage.removeItem(SS_KEYS.id);
    sessionStorage.removeItem(SS_KEYS.seq);
    sessionStorage.removeItem(SS_KEYS.repo);
    sessionIdRef.current = null;
    lastSeqRef.current = 0;
  };

  // ---- repo list ----
  const loadRepos = useCallback(async () => {
    const auth = token.trim();
    if (!auth) {
      setStatus('Enter the agent token first.');
      return;
    }
    try {
      const r = await fetch(`${backendUrl.trim()}/repos`, {
        headers: { 'x-agent-token': auth },
      });
      if (!r.ok)
        throw new Error(
          r.status === 401
            ? `bad token (401) — field has ${auth.length} chars; check it matches AGENT_TOKEN`
            : `repos ${r.status}`
        );
      const data = await r.json();
      setRepos(data.repos || []);
      if (data.repos?.length && !selectedRepo) setSelectedRepo(data.repos[0].path);
      setStatus(`${data.repos?.length || 0} repo(s) available`);
    } catch (e) {
      setStatus(`Could not reach backend: ${e.message}`);
    }
  }, [backendUrl, token, selectedRepo]);

  // ---- TTS playback queue ----
  const playNext = useCallback(async () => {
    if (playingRef.current || muted) return;
    const text = ttsQueueRef.current.shift();
    if (!text) return;
    playingRef.current = true;
    try {
      const r = await fetch(`${backendUrl.trim()}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-agent-token': token.trim() },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) throw new Error(`tts ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        playingRef.current = false;
        playNext();
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        playingRef.current = false;
        playNext();
      };
      await audio.play();
    } catch {
      playingRef.current = false;
      playNext();
    }
  }, [backendUrl, token, muted]);

  const speak = useCallback(
    (text) => {
      if (!text?.trim() || muted) return;
      ttsQueueRef.current.push(text.trim());
      playNext();
    },
    [playNext, muted]
  );

  const stopAudio = useCallback(() => {
    ttsQueueRef.current = [];
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    playingRef.current = false;
  }, []);

  // ---- WS connection ----
  const handleMessage = useCallback(
    (msg) => {
      // Drop already-seen sequenced events (resume replay / duplicate frames).
      // 'ready' keeps its own seq handling below (it resets the counter on a fresh session).
      if (msg.seq != null && msg.type !== 'ready') {
        if (msg.seq <= lastSeqRef.current) return;
        lastSeqRef.current = msg.seq;
        sessionStorage.setItem(SS_KEYS.seq, String(msg.seq));
      }
      switch (msg.type) {
        case 'ready':
          sessionIdRef.current = msg.sessionId || null;
          lastSeqRef.current = msg.seq || 0;
          persistSession(msg.sessionId, msg.seq || 0, msg.repo?.path || '');
          setSessionReady(true);
          setStatus(`Ready — working in ${msg.repo?.name}`);
          break;
        case 'resumed':
          setSessionReady(true);
          setStatus(
            `Resumed — working in ${msg.repo?.name}${
              msg.gap ? ' (some earlier output was trimmed)' : ''
            }`
          );
          break;
        case 'resume-failed':
          clearSession();
          if (selectedRepoRef.current) {
            setStatus('Previous session expired — starting fresh.');
            wsRef.current?.send(
              JSON.stringify({ type: 'start', repo: selectedRepoRef.current })
            );
          } else {
            setStatus('Session expired — pick a repo and Connect.');
          }
          break;
        case 'pong':
          break;
        case 'text-delta':
          liveBufRef.current += msg.text;
          setInterim(''); // user's turn is done; show assistant live text instead
          setStatus(liveBufRef.current.slice(-120));
          break;
        case 'assistant-text':
          if (msg.replay) {
            // Replayed text is rendered but never re-spoken.
            liveBufRef.current = '';
            setTranscript((t) => [...t, { who: 'delta', text: msg.text }]);
            break;
          }
          liveBufRef.current = '';
          setTranscript((t) => [...t, { who: 'delta', text: msg.text }]);
          speak(msg.text);
          break;
        case 'tool':
          setActivity(({ items, total }) => ({
            items: [...items, { tool: msg.tool, summary: msg.summary }].slice(-200),
            total: total + 1,
          }));
          break;
        case 'permission-request':
          // Dedup by id so re-emitted (replay) prompts never stack.
          setPerms((p) => (p.some((x) => x.id === msg.id) ? p : [...p, msg]));
          if (!msg.replay) {
            setStatus(`Waiting for your OK: ${msg.title}`);
            speak('I need your confirmation to run a command.');
          }
          break;
        case 'result':
          setStatus(msg.isError ? 'Turn ended with an error.' : 'Done.');
          break;
        case 'error':
          setStatus(`Error: ${msg.message}`);
          setTranscript((t) => [...t, { who: 'error', text: msg.message }]);
          break;
        default:
          break;
      }
    },
    [speak]
  );
  handleMessageRef.current = handleMessage;

  // Clear all keepalive/connect timers (but not the reconnect schedule).
  const stopTimers = useCallback(() => {
    for (const r of [keepaliveTimerRef, heartbeatTimerRef]) {
      if (r.current) {
        clearInterval(r.current);
        r.current = null;
      }
    }
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  }, []);

  // App-level keepalive (browser WebSocket can't send protocol pings) plus a
  // staleness watchdog: any inbound frame refreshes lastActivityRef; >45s of
  // silence on an OPEN socket forces a close -> onclose -> reconnect.
  const startKeepalive = useCallback(() => {
    heartbeatTimerRef.current = setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ping', t: Date.now() }));
        } catch {
          /* noop */
        }
      }
    }, CLIENT_PING_INTERVAL_MS);
    keepaliveTimerRef.current = setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastActivityRef.current > STALE_AFTER_MS) {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      }
    }, STALE_CHECK_INTERVAL_MS);
  }, []);

  // Reconnect with exponential backoff + jitter after an unexpected disconnect.
  const scheduleReconnect = useCallback(() => {
    if (intentionalCloseRef.current || reconnectTimerRef.current) return;
    const attempt = reconnectAttemptsRef.current;
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      setConnStatus('failed');
      setStatus('Connection lost. Click Reconnect.');
      return;
    }
    const base = Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
    const delay = Math.round(base * (0.5 + Math.random() * 0.5));
    reconnectAttemptsRef.current = attempt + 1;
    setReconnectAttempt(attempt + 1);
    setConnStatus('reconnecting');
    setStatus(`Reconnecting (attempt ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS})…`);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectSocketRef.current?.();
    }, delay);
  }, []);

  // Detach handlers and close the current socket so its async onclose can't
  // clobber a fresh connection's state.
  const teardownSocket = useCallback(() => {
    const old = wsRef.current;
    if (!old) return;
    old.onopen = old.onmessage = old.onerror = old.onclose = null;
    try {
      old.close();
    } catch {
      /* noop */
    }
    wsRef.current = null;
  }, []);

  const connectSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    intentionalCloseRef.current = false;
    teardownSocket();
    setConnStatus(reconnectAttemptsRef.current > 0 ? 'reconnecting' : 'connecting');
    let ws;
    try {
      ws = new WebSocket(wsUrlFrom(backendUrl.trim(), token.trim()));
    } catch {
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;
    connectTimeoutRef.current = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      }
    }, CONNECT_TIMEOUT_MS);
    ws.onopen = () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      setReconnectAttempt(0);
      lastActivityRef.current = Date.now();
      setConnStatus('connected');
      startKeepalive();
      if (sessionIdRef.current) {
        setStatus('Connected. Resuming session…');
        ws.send(
          JSON.stringify({
            type: 'resume',
            sessionId: sessionIdRef.current,
            lastSeq: lastSeqRef.current,
          })
        );
      } else {
        setStatus('Connected. Starting session…');
        ws.send(JSON.stringify({ type: 'start', repo: selectedRepoRef.current }));
      }
    };
    ws.onmessage = (e) => {
      lastActivityRef.current = Date.now();
      let data;
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }
      try {
        handleMessageRef.current?.(data);
      } catch (err) {
        console.error('RepoConsole message error:', err);
      }
    };
    ws.onerror = () => setStatus('WebSocket error — is the backend running?');
    ws.onclose = () => {
      if (wsRef.current !== ws) return; // a newer socket already took over
      wsRef.current = null;
      stopTimers();
      setSessionReady(false);
      if (intentionalCloseRef.current) {
        setConnStatus('idle');
        setStatus('Disconnected.');
      } else {
        scheduleReconnect();
      }
    };
  }, [backendUrl, token, scheduleReconnect, startKeepalive, stopTimers, teardownSocket]);
  connectSocketRef.current = connectSocket;

  const connect = useCallback(() => {
    if (!token.trim() || (!selectedRepo && !sessionIdRef.current)) {
      setStatus('Pick a repo and enter the token first.');
      return;
    }
    reconnectAttemptsRef.current = 0;
    setReconnectAttempt(0);
    connectSocket();
  }, [token, selectedRepo, connectSocket]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: 'stop' }));
      } catch {
        /* noop */
      }
    }
    clearSession();
    stopTimers();
    teardownSocket();
    setConnStatus('idle');
    setSessionReady(false);
    setStatus('Disconnected.');
  }, [stopTimers, teardownSocket]);

  const sendSay = useCallback(
    (text) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setStatus(
          reconnectTimerRef.current || connStatus === 'reconnecting'
            ? 'Reconnecting — try again in a moment.'
            : 'Not connected.'
        );
        return;
      }
      stopAudio(); // barge-in: stop whatever it was saying
      ws.send(JSON.stringify({ type: 'interrupt' }));
      ws.send(JSON.stringify({ type: 'say', text }));
      setTranscript((t) => [...t, { who: 'you', text }]);
      setInterim('');
    },
    [stopAudio, connStatus]
  );

  const respondPerm = useCallback((id, decision) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'permission', id, decision }));
    }
    setPerms((p) => p.filter((x) => x.id !== id));
  }, []);

  // ---- Speech-to-text (browser Web Speech API) ----
  const startMic = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setStatus('This browser has no Web Speech API. Use Chrome, or type below.');
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interimText += res[0].transcript;
      }
      if (interimText) setInterim(interimText);
      if (finalText.trim()) sendSay(finalText.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setListening(false);
      setStatus(`Mic error: ${e.error}`);
    };
    recognitionRef.current = rec;
    stopAudio(); // don't transcribe our own voice
    rec.start();
    setListening(true);
    setStatus('Listening…');
  }, [sendSay, stopAudio]);

  const stopMic = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  // Auto-resume on mount (handles CRA full-page reloads when the agent edits
  // this very repo). Safe under StrictMode's double-mount: connectSocket resets
  // intentionalCloseRef and the unmount cleanup is idempotent.
  useEffect(() => {
    const storedRepo = sessionStorage.getItem(SS_KEYS.repo);
    if (storedRepo) setSelectedRepo((prev) => prev || storedRepo);
    if (sessionIdRef.current && (localStorage.getItem('agentToken') || ENV_TOKEN).trim()) {
      setStatus('Resuming previous session…');
      connectSocketRef.current?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Teardown only on real unmount. All three deps are []-dep useCallbacks, so
  // this effect never re-runs mid-life. It does NOT send 'stop' and does NOT
  // clear sessionStorage, so navigating back to /coder within the grace window
  // resumes the session; teardownSocket detaches handlers before close so no
  // setState fires after unmount.
  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      stopTimers();
      teardownSocket();
      recognitionRef.current?.stop();
      stopAudio();
    };
  }, [stopTimers, teardownSocket, stopAudio]);

  const [typed, setTyped] = useState('');

  const dotClass =
    connStatus === 'connected'
      ? sessionReady
        ? 'on'
        : 'mid'
      : connStatus === 'connecting' || connStatus === 'reconnecting'
        ? 'warn'
        : connStatus === 'failed'
          ? 'err'
          : 'off';

  return (
    <div className="rc-root">
      <header className="rc-header">
        <h1>🎙️ Repo Coder</h1>
        <span className={`rc-dot ${dotClass}`} />
        <span className="rc-status">{status}</span>
      </header>

      {(connStatus === 'reconnecting' || connStatus === 'failed') && (
        <div className={`rc-connbar ${connStatus}`}>
          {connStatus === 'reconnecting' ? (
            <span>
              Reconnecting (attempt {reconnectAttempt}/{MAX_RECONNECT_ATTEMPTS})… your session
              will resume automatically.
            </span>
          ) : (
            <>
              <span>Connection lost.</span>
              <button className="rc-primary" onClick={connect}>
                Reconnect
              </button>
            </>
          )}
        </div>
      )}

      <section className="rc-setup">
        <div className="rc-field">
          <label>Backend</label>
          <input
            value={backendUrl}
            onChange={(e) => {
              setBackendUrl(e.target.value);
              persist('agentBackendUrl', e.target.value);
            }}
          />
        </div>
        <div className="rc-field">
          <label>Token</label>
          <input
            type="text"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={token}
            placeholder="AGENT_TOKEN"
            onChange={(e) => {
              const v = e.target.value.trim();
              setToken(v);
              persist('agentToken', v);
            }}
          />
        </div>
        <button onClick={loadRepos}>Load repos</button>
        <div className="rc-field">
          <label>Repo</label>
          <select
            value={selectedRepo}
            disabled={connStatus !== 'idle'}
            onChange={(e) => setSelectedRepo(e.target.value)}
          >
            <option value="">— select —</option>
            {repos.map((r) => (
              <option key={r.path} value={r.path}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        {connStatus !== 'idle' ? (
          <button className="rc-danger" onClick={disconnect}>
            Disconnect
          </button>
        ) : (
          <button
            className="rc-primary"
            onClick={connect}
            disabled={!token.trim() || (!selectedRepo && !sessionIdRef.current)}
          >
            Connect
          </button>
        )}
        <button className="rc-ghost" onClick={() => setMuted((m) => !m)}>
          {muted ? '🔇 Muted' : '🔊 Voice on'}
        </button>
      </section>

      {perms.length > 0 && (
        <section className="rc-perms">
          {perms.map((p) => (
            <div key={p.id} className="rc-perm">
              <div className="rc-perm-title">⚠️ {p.title}</div>
              {p.command && <code className="rc-perm-cmd">{p.command}</code>}
              {p.reason && <div className="rc-perm-reason">matched: {p.reason}</div>}
              <div className="rc-perm-actions">
                <button className="rc-primary" onClick={() => respondPerm(p.id, 'allow')}>
                  Allow
                </button>
                <button className="rc-danger" onClick={() => respondPerm(p.id, 'deny')}>
                  Deny
                </button>
              </div>
            </div>
          ))}
        </section>
      )}

      <div className="rc-body">
        <section className="rc-conversation">
          <h2>Conversation</h2>
          <div className="rc-scrollwrap">
            <div className="rc-log" ref={convScroll.containerRef}>
              {transcript.map((m, i) => (
                <div key={i} className={`rc-msg rc-${m.who}`}>
                  <span className="rc-who">
                    {m.who === 'you' ? 'You' : m.who === 'delta' ? 'Delta' : '⚠️'}
                  </span>
                  <span className="rc-text">{m.text}</span>
                </div>
              ))}
              {interim && <div className="rc-msg rc-you rc-interim">{interim}…</div>}
            </div>
            {!convScroll.isPinned && (
              <button className="rc-jump" onClick={convScroll.jumpToLatest}>
                ↓ {convScroll.newCount > 0 ? `${convScroll.newCount} new` : 'Latest'}
              </button>
            )}
          </div>

          <div className="rc-mic-row">
            <button
              className={`rc-mic ${listening ? 'live' : ''}`}
              onMouseDown={startMic}
              onMouseUp={stopMic}
              onTouchStart={startMic}
              onTouchEnd={stopMic}
              disabled={!sessionReady}
              title="Hold to talk"
            >
              {listening ? '● Listening' : '🎤 Hold to talk'}
            </button>
            <form
              className="rc-type"
              onSubmit={(e) => {
                e.preventDefault();
                if (typed.trim()) {
                  sendSay(typed.trim());
                  setTyped('');
                }
              }}
            >
              <input
                value={typed}
                placeholder={sessionReady ? 'or type a command…' : 'connect first'}
                disabled={!sessionReady}
                onChange={(e) => setTyped(e.target.value)}
              />
              <button type="submit" disabled={!sessionReady}>
                Send
              </button>
            </form>
          </div>
        </section>

        <aside className="rc-activity">
          <h2>Activity</h2>
          <div className="rc-scrollwrap">
            <div className="rc-activity-log" ref={actScroll.containerRef}>
              {activity.items.length === 0 && <div className="rc-empty">No actions yet.</div>}
              {activity.items.map((a, i) => (
                <div key={i} className="rc-action">
                  <span className="rc-tool">{a.tool}</span>
                  <span className="rc-summary">{a.summary}</span>
                </div>
              ))}
            </div>
            {!actScroll.isPinned && (
              <button className="rc-jump" onClick={actScroll.jumpToLatest}>
                ↓ {actScroll.newCount > 0 ? `${actScroll.newCount} new` : 'Latest'}
              </button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
